import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";

import { RootState } from "store";
import Species from "api/types/models/species";
import { setPagination } from "slices/vocabularyFilters";
import { Pagination, PencilIcon, Table, Tag, Toggle } from "@mwi/ui";
import { useGetSpeciesQuery, useUpdateSingleSpeciesMutation } from "api";
import EditSpeciesVocabularyModal from "components/modals/admin/vocabulary/species/edit";

const SpeciesVocabulary = (): JSX.Element => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { id } = useParams();

  const { filtering, pagination } = useSelector((state: RootState) => ({
    filtering: state.vocabularyFilters.filtering,
    pagination: state.vocabularyFilters.pagination,
  }));

  const { data: species, isFetching: fetchingSpecies } = useGetSpeciesQuery({
    page: pagination.page,
    size: pagination.size,
    includeDisabled: true,
    search: filtering.search,
  });

  const [updateSpecies] = useUpdateSingleSpeciesMutation();

  const handleSpeciesToggle = (singleSpecies: Species, value: boolean) => {
    updateSpecies({
      speciesUuid: singleSpecies.id,
      body: {
        enabled: value,
        translation: singleSpecies.translation,
      },
    });
  };

  return (
    <>
      <div className="w-full overflow-x-auto">
        <Table loading={fetchingSpecies}>
          <Table.Head>
            <Table.Row>
              <Table.Item as="th">Species</Table.Item>
              <Table.Item as="th">Translation</Table.Item>
              <Table.Item as="th">Active</Table.Item>
              <Table.Item as="th">Edit</Table.Item>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {species && species?.data?.length ? (
              species.data.map((singleSpecies) => (
                <Table.Row key={singleSpecies.id}>
                  <Table.Item>{singleSpecies.name}</Table.Item>
                  <Table.Item>
                    {singleSpecies.translation ? (
                      singleSpecies.translation
                    ) : (
                      <Tag variants="outlined">N/A</Tag>
                    )}
                  </Table.Item>
                  <Table.Item>
                    <Toggle
                      value={singleSpecies.enabled}
                      onChange={(value) =>
                        handleSpeciesToggle(singleSpecies, value)
                      }
                    />
                  </Table.Item>
                  <Table.Item>
                    <Link
                      className="inline-block"
                      to={`/admin/customisations/vocabulary/${singleSpecies.id}`}
                    >
                      <PencilIcon className="w-6 h-6 cursor-pointer text-primary" />
                    </Link>
                  </Table.Item>
                </Table.Row>
              ))
            ) : (
              <Table.Row>
                <Table.Item colSpan={4} className="text-center">
                  No species found
                </Table.Item>
              </Table.Row>
            )}
          </Table.Body>
        </Table>
      </div>

      <div className="mt-4 col-span-full">
        <Pagination
          onPrevPage={() =>
            dispatch(setPagination({ page: pagination.page - 1 }))
          }
          prevPage={pagination.page > 1}
          currentPage={pagination.page}
          nextPage={species?.meta?.has_next_page ?? false}
          onNextPage={() =>
            dispatch(setPagination({ page: pagination.page + 1 }))
          }
          loading={fetchingSpecies}
        />
      </div>

      <EditSpeciesVocabularyModal
        isOpen={!!id && filtering.category === "species"}
        onClose={() => navigate("/admin/customisations/vocabulary")}
      />
    </>
  );
};

export default SpeciesVocabulary;
